import React, { useId, useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { ButtonWithIcon } from '@els/els-react--button';
import { ModalPanel } from '@els/els-react--modal-panel';

import Resizable from '@Components/Resizable';
import { DIRECTION_LEFT, DIRECTION_RIGHT } from '@Components/Resizable/constants';

import ResultsFiltersRail from './ResultsFiltersRail';
import {
  DRAWER_VIEW_ASSISTANT,
  DRAWER_VIEW_FILTERS,
  PANEL_WIDTH_DEFAULT,
  PANEL_WIDTH_FOCUSED,
  RESULTS_FILTERS_RAIL_WIDTH,
} from './constants';

import './Drawer.scss';

// Literal, FE-owned titles — spec (FR-7.4) requires exactly these two strings, with no count.
const VIEW_TITLE = {
  [DRAWER_VIEW_ASSISTANT]: 'AI Assistant',
  [DRAWER_VIEW_FILTERS]: 'Filters',
};

// Live-region copy for a view switch (FR-7.3) — exact spec wording.
const VIEW_ANNOUNCEMENT = {
  [DRAWER_VIEW_ASSISTANT]: 'Assistant selected.',
  [DRAWER_VIEW_FILTERS]: 'Filters selected.',
};

// This drawer is left-docked (FR-7.4); the resize handle belongs on the panel's own edge that
// faces away from the screen edge it's anchored to. Kept symmetric for a hypothetical
// right-docked usage, mirroring src/Pages/RetroSynthesis/.../PlacementDocked.jsx's mapping.
const RESIZE_DIRECTION_BY_DOCK_SIDE = {
  left: DIRECTION_RIGHT,
  right: DIRECTION_LEFT,
};

function useFocusModeMaxWidth(isFocusMode) {
  const getMaxPanelWidth = () => {
    if (typeof window === 'undefined') return PANEL_WIDTH_DEFAULT;

    return Math.max(PANEL_WIDTH_DEFAULT, Math.floor(window.innerWidth / 2) - RESULTS_FILTERS_RAIL_WIDTH);
  };
  const [maxWidth, setMaxWidth] = useState(getMaxPanelWidth);

  useEffect(() => {
    if (!isFocusMode || typeof window === 'undefined') return undefined;

    const handleResize = () => setMaxWidth(getMaxPanelWidth());
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [isFocusMode]);

  return maxWidth;
}

/**
 * Assistant / Filters drawer shell (spec Feature 7).
 *
 * Purely presentational/controlled — no Redux, no data fetching. The caller owns:
 * - which views exist (`assistantEligible`) and which one is showing (`activeView`/`onViewChange`)
 * - whether the panel is expanded (`isOpen`/`setIsOpen`)
 * - whether the panel is in focus mode / 460px (`isFocusMode`)
 * - whether the Assistant nav item shows its filter-applied dot (`hasFilterApplied`)
 * - the content rendered inside each view (`assistantContent`/`filtersContent`, or `children` as a
 *   single-view fallback for a caller that never sets `assistantEligible`)
 */
function Drawer({
  direction = 'left',
  isOpen = true,
  setIsOpen = () => {},
  assistantEligible = false,
  activeView,
  onViewChange = () => {},
  onViewClick = () => {},
  onCollapse = () => {},
  hasFilterApplied = false,
  isFocusMode = false,
  onResize,
  onResizeEnd,
  assistantContent,
  filtersContent,
  header,
  headingRef,
  rootRef,
  renderPanelAsModal = false,
  children,
}) {
  const panelId = useId();
  const panelRef = useRef(null);
  const toggleRef = useRef(null);
  const lastTriggerRef = useRef(null);
  const wasOpenRef = useRef(isOpen);

  const defaultView = assistantEligible ? DRAWER_VIEW_ASSISTANT : DRAWER_VIEW_FILTERS;
  const [internalView, setInternalView] = useState(defaultView);
  const isViewControlled = activeView !== undefined;
  const currentView = isViewControlled ? activeView : internalView;

  const [announcement, setAnnouncement] = useState('');

  // Uncontrolled-mode safety net: if eligibility flips off while Assistant is showing, fall back
  // to Filters rather than rendering a view the rail no longer offers a nav item for.
  useEffect(() => {
    if (!isViewControlled && !assistantEligible && internalView === DRAWER_VIEW_ASSISTANT) {
      setInternalView(DRAWER_VIEW_FILTERS);
    }
  }, [assistantEligible, isViewControlled, internalView]);

  const changeView = useCallback(
    (nextView, triggerEl) => {
      onViewClick(nextView);
      if (triggerEl) {
        lastTriggerRef.current = triggerEl;
      }
      if (nextView !== currentView) {
        if (!isViewControlled) {
          setInternalView(nextView);
        }
        setAnnouncement(VIEW_ANNOUNCEMENT[nextView]);
        onViewChange(nextView);
      }
      if (!isOpen) {
        setIsOpen(true);
      }
    },
    [currentView, isOpen, isViewControlled, onViewChange, onViewClick, setIsOpen]
  );

  const handleToggleClick = (event) => {
    lastTriggerRef.current = event.currentTarget;
    if (isOpen) {
      onCollapse();
    }
    setIsOpen(!isOpen);
  };

  const closePanel = useCallback(() => setIsOpen(false), [setIsOpen]);

  // Listen at document bubble phase so Escape still closes the drawer after the user has moved
  // focus back into the interactive results list. Dialog/menu targets are ignored: their existing
  // capture-phase handlers get the first Escape and the drawer closes on a subsequent press.
  useEffect(() => {
    if (!isOpen || renderPanelAsModal || typeof document === 'undefined') return undefined;

    const handleEscape = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (event.target instanceof Element && event.target.closest('[role="dialog"], [role="menu"], .c-els-flyout')) return;
      setIsOpen(false);
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, renderPanelAsModal, setIsOpen]);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current && !renderPanelAsModal) {
      panelRef.current?.focus();
    } else if (!isOpen && wasOpenRef.current) {
      const focusTarget = lastTriggerRef.current || toggleRef.current;
      focusTarget?.focus({ preventScroll: renderPanelAsModal });
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, renderPanelAsModal]);

  const assistantFocusMode = isFocusMode && currentView === DRAWER_VIEW_ASSISTANT;
  const resizableFocusMode = assistantFocusMode && !renderPanelAsModal;
  const maxFocusWidth = useFocusModeMaxWidth(resizableFocusMode);

  const title = header || VIEW_TITLE[currentView];

  // The persistent rail is a labelled control group; the desktop panel is a separately labelled
  // region. Keep the "panel" suffix so the region's name describes the destination controlled by
  // the rail buttons, while the visible heading remains exactly "AI Assistant"/"Filters".
  const panelRegionLabel = `${title} panel`;

  const panelInner = (
    <div
      id={renderPanelAsModal ? undefined : panelId}
      ref={panelRef}
      tabIndex={renderPanelAsModal ? undefined : -1}
      role={renderPanelAsModal ? undefined : 'region'}
      aria-label={renderPanelAsModal ? undefined : panelRegionLabel}
      className="drawer-panel__inner">
      <div className="drawer-panel__header">
        <h2 ref={headingRef} tabIndex={-1} className="u-els-font-size-h4 rx-focusable">
          {title}
        </h2>
        {renderPanelAsModal && (
          <ButtonWithIcon
            className="drawer-panel__collapse"
            data-e2e="drawer-panel-collapse-button"
            type="borderless"
            sprite="NavCollapse"
            onClick={closePanel}
            iconOnly
            size="small">
            Close {title} panel
          </ButtonWithIcon>
        )}
      </div>
      <div className="drawer-panel__content">
        {currentView === DRAWER_VIEW_ASSISTANT ? (assistantContent ?? children) : (filtersContent ?? children)}
      </div>
      {/* eslint-disable-next-line jsx-a11y/prefer-tag-over-role */}
      <div role="status" aria-live="polite" className="u-els-hide-visually" data-testid="drawer-live-region">
        {announcement}
      </div>
    </div>
  );

  const panelClassName = classnames('drawer-panel', `drawer-panel-${direction}`, {
    'drawer-panel--focus-mode': assistantFocusMode,
    'drawer-panel--modal': renderPanelAsModal,
  });
  const modalPanelWidth = assistantFocusMode ? PANEL_WIDTH_FOCUSED : PANEL_WIDTH_DEFAULT;

  let expandedPanel = null;
  if (isOpen && renderPanelAsModal) {
    expandedPanel = (
      <ModalPanel
        id={panelId}
        className="drawer-panel-modal"
        panelPosition={direction}
        modalProps={{
          focusTrapProps: {
            focusTrapOptions: { returnFocusOnDeactivate: false },
          },
          label: title,
          onClose: closePanel,
          shouldHideCloseButton: true,
          width: `${modalPanelWidth}px`,
        }}>
        <div className={panelClassName}>{panelInner}</div>
      </ModalPanel>
    );
  } else if (isOpen) {
    expandedPanel = (
      <Resizable
        direction={RESIZE_DIRECTION_BY_DOCK_SIDE[direction] || DIRECTION_RIGHT}
        className={panelClassName}
        constraints={{ widthMin: PANEL_WIDTH_DEFAULT, widthMax: maxFocusWidth }}
        disabled={!resizableFocusMode}
        initializeSize={false}
        resizeHandleLabel={`Resize ${title} panel`}
        onResize={onResize}
        onDragEnd={onResizeEnd}
        style={resizableFocusMode ? { maxWidth: `${maxFocusWidth}px` } : undefined}>
        {panelInner}
      </Resizable>
    );
  }

  // Desktop/tablet panels leave results interactive. The narrow-screen ModalPanel intentionally
  // owns the backdrop and focus trap; reference navigation closes it before scrolling results.
  return (
    <div ref={rootRef} className={classnames('drawer', `drawer-${direction}`, { 'drawer-open': isOpen })}>
      <ResultsFiltersRail
        activeView={currentView}
        assistantEligible={assistantEligible}
        hasAssistantUpdate={hasFilterApplied}
        isOpen={isOpen}
        onSelectView={changeView}
        onToggle={handleToggleClick}
        panelId={panelId}
        toggleRef={toggleRef}
      />

      {expandedPanel}
    </div>
  );
}

Drawer.propTypes = {
  /** Which screen edge the drawer is docked to. */
  direction: PropTypes.oneOf(['left', 'right']),
  /** Whether the content panel is expanded. The 84px rail is always rendered regardless. */
  isOpen: PropTypes.bool,
  /** Called with the next `isOpen` value from the toggle button, a nav item, or Escape. */
  setIsOpen: PropTypes.func,
  /** Whether the Assistant view/nav item exists at all (FR-7.1). Filters-only when false. */
  assistantEligible: PropTypes.bool,
  /** Controlled active view (`DRAWER_VIEW_ASSISTANT` | `DRAWER_VIEW_FILTERS`). Optional — falls
   * back to internal state (defaulting to Assistant when eligible, else Filters) when omitted. */
  activeView: PropTypes.oneOf([DRAWER_VIEW_ASSISTANT, DRAWER_VIEW_FILTERS]),
  /** Called with the new view whenever a nav item switches the active view. */
  onViewChange: PropTypes.func,
  /** Called with the clicked view on every rail nav click, including reselects and reopen actions. */
  onViewClick: PropTypes.func,
  /** Called when the rail toggle collapses an open panel. */
  onCollapse: PropTypes.func,
  /** Shows the decorative "filter applied" dot on the Assistant nav item (FR-7.3). */
  hasFilterApplied: PropTypes.bool,
  /** Widens the panel to 460px and enables drag-resize (FR-7.4). */
  isFocusMode: PropTypes.bool,
  /** Passthrough to Resizable's `onResize`, fired while dragging in focus mode. */
  onResize: PropTypes.func,
  /** Passthrough to Resizable's `onDragEnd`, fired when a drag-resize ends. */
  onResizeEnd: PropTypes.func,
  /** Content rendered when the Assistant view is active. */
  assistantContent: PropTypes.node,
  /** Content rendered when the Filters view is active. */
  filtersContent: PropTypes.node,
  /** Optional visible header/accessible-name override; defaults to "AI Assistant" / "Filters". */
  header: PropTypes.string,
  /** Optional programmatic-focus target for focus recovery after dynamic panel actions. */
  headingRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  /** External ref to the drawer shell, used by Results to set its available viewport height. */
  rootRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  /** Renders the expanded content in a focus-trapped ModalPanel on narrow viewports. */
  renderPanelAsModal: PropTypes.bool,
  /** Legacy/fallback content slot, rendered when the active view has no explicit content prop. */
  children: PropTypes.node,
};

export default Drawer;
